import React, { useState } from 'react';
import { TaskRecommendationImporter } from './TaskRecommendationImporter';
import { EnhancedFeedbackDropdown } from './EnhancedFeedbackDropdown';
import { useBulkTaskActions } from '@/hooks/goal-planner/useBulkTaskActions';
import { useSuggestedTasksQuery } from '@/hooks/goal-planner/queries/use-suggested-tasks-query';
import { SuggestedTask } from '@/hooks/goal-planner/suggested-task-types';
import { supabase } from '@/integrations/supabase/client';
import TaskGenerationContextModal, { type UserContext } from './TaskGenerationContextModal';
import { useBulkTaskGeneration } from '@/hooks/goal-planner/useBulkTaskGeneration';
import { toast } from 'sonner';

/**
 * Send rejection feedback to OpenAI thread for learning
 */
async function sendRejectionFeedbackToThread(
  threadId: string,
  rejectedTasks: SuggestedTask[],
  userFeedback: string
): Promise<void> {
  try {
    console.log('[FeedbackContext] Sending rejection feedback to OpenAI thread:', {
      threadId,
      taskCount: rejectedTasks.length,
      feedback: userFeedback.substring(0, 100) + '...'
    });

    // Build context message with rejected task details
    const taskList = rejectedTasks.map(task => `- "${task.title}": ${task.description}`).join('\n');
    
    const contextMessage = `USER TASK REJECTION FEEDBACK:

The user has rejected the following ${rejectedTasks.length} task suggestion${rejectedTasks.length > 1 ? 's' : ''}:

${taskList}

User's reasoning: "${userFeedback}"

Please avoid suggesting similar tasks in the future. Consider this feedback when generating new task recommendations to ensure better relevance and alignment with the user's goals and preferences.`;

    // Send context to unified threaded-text-generation endpoint
    console.log('[FeedbackContext] Sending feedback via unified endpoint');
    const { data, error } = await supabase.functions.invoke('threaded-text-generation', {
      body: {
        type: 'add-context',
        thread_id: threadId,
        context_type: 'task_rejection_feedback',
        context_data: {
          rejected_tasks: rejectedTasks.map(task => ({
            title: task.title,
            description: task.description
          })),
          user_feedback: userFeedback,
          timestamp: new Date().toISOString()
        },
        context_message: contextMessage,
        should_run: true, // Process the context with AI
        appName: 'Task Manager',
        functionName: 'Task Rejection Feedback'
      }
    });

    if (error) {
      console.error('[FeedbackContext] Failed to send feedback to thread:', error);
      throw new Error('Failed to send feedback context to OpenAI');
    }

    console.log('[FeedbackContext] ✅ Successfully sent feedback via unified endpoint');
    
  } catch (error) {
    console.error('[FeedbackContext] Error sending rejection feedback:', error);
    // Don't throw - we don't want feedback transmission failures to block task deletion
    // The user experience should continue even if context sync fails
  }
}

interface TaskRecommendationContainerProps {
  goalId: string;
  goalTitle: string;
  goalDescription?: string;
  openaiThreadId?: string;
  className?: string;
  hideClearSelection?: boolean; // Hide clear selection button for simplified UX
  contextInput?: string; // NEW: User context for AI generation
  onContextInputChange?: (value: string) => void; // NEW: Context change handler
  isInitialGeneration?: boolean; // NEW: Whether this is the first generation (show modal)
}

interface FeedbackState {
  isOpen: boolean;
  selectedTaskIds: string[];
  selectedTasks: SuggestedTask[];
  type: 'approve' | 'reject';
  isProcessing: boolean;
}

export const TaskRecommendationContainer: React.FC<TaskRecommendationContainerProps> = ({
  goalId,
  goalTitle,
  goalDescription,
  openaiThreadId,
  className,
  hideClearSelection = false,
  contextInput,
  onContextInputChange,
  isInitialGeneration = false
}) => {
  const [feedbackState, setFeedbackState] = useState<FeedbackState>({
    isOpen: false,
    selectedTaskIds: [],
    selectedTasks: [],
    type: 'reject',
    isProcessing: false
  });
  
  // State for context modal (only used in initial generation)
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);

  // Get task data for task lookup
  const { data: allTasks = [] } = useSuggestedTasksQuery(goalId);

  // Bulk actions
  const { 
    addToProject, 
    clearSelection, 
    isAddingToProject, 
    isClearingSelection 
  } = useBulkTaskActions({ goalId });
  
  // Bulk generation hook
  const bulkGenerationMutation = useBulkTaskGeneration(goalId);

  // Handle bulk add to project (no feedback needed)
  const handleBulkAddToProject = async (selectedTaskIds: string[]) => {
    try {
      // Get the actual task objects
      const selectedTasks = allTasks.filter(task => selectedTaskIds.includes(task.id));
      
      console.log('[TaskRecommendationContainer] Adding tasks to project:', selectedTasks.length);
      await addToProject(selectedTaskIds, selectedTasks);
    } catch (error) {
      console.error('[TaskRecommendationContainer] Failed to add tasks to project:', error);
      throw error;
    }
  };

  // Handle bulk clear selection (requires feedback)
  const handleBulkClearSelection = (selectedTaskIds: string[], selectedTasks: SuggestedTask[]) => {
    console.log('[TaskRecommendationContainer] Initiating feedback for cleared tasks:', selectedTasks.length);
    
    setFeedbackState({
      isOpen: true,
      selectedTaskIds,
      selectedTasks,
      type: 'reject',
      isProcessing: false
    });
  };

  // Handle feedback submission (synchronous with loading states)
  const handleFeedbackSubmit = async (feedback: string) => {
    // Set processing state to block UI
    setFeedbackState(prev => ({ ...prev, isProcessing: true }));
    
    try {
      console.log('[TaskRecommendationContainer] Processing feedback and clearing tasks...');
      console.log('Feedback:', feedback);
      console.log('Task count:', feedbackState.selectedTasks.length);
      
      // Step 1: Send feedback context to OpenAI thread (SYNCHRONOUS)
      if (openaiThreadId) {
        console.log('[TaskRecommendationContainer] Sending feedback to OpenAI thread...');
        await sendRejectionFeedbackToThread(
          openaiThreadId,
          feedbackState.selectedTasks,
          feedback
        );
        console.log('[TaskRecommendationContainer] ✅ Feedback sent to OpenAI thread');
      } else {
        console.warn('[TaskRecommendationContainer] No OpenAI thread ID available - skipping feedback context');
      }
      
      // Step 2: Clear the rejected tasks
      console.log('[TaskRecommendationContainer] Clearing rejected tasks...');
      await clearSelection(feedbackState.selectedTaskIds);
      console.log('[TaskRecommendationContainer] ✅ Tasks cleared from suggestions');
      
      // Step 3: Close feedback modal and reset processing state
      setFeedbackState(prev => ({ 
        ...prev, 
        isOpen: false, 
        isProcessing: false 
      }));
      
      console.log('[TaskRecommendationContainer] ✅ Feedback processed successfully - UI ready for new operations');
      
    } catch (error) {
      console.error('[TaskRecommendationContainer] Failed to process feedback:', error);
      
      // Reset processing state on error
      setFeedbackState(prev => ({ ...prev, isProcessing: false }));
      throw error;
    }
  };

  // Close feedback modal (only if not processing)
  const handleCloseFeedback = () => {
    if (!feedbackState.isProcessing) {
      setFeedbackState(prev => ({ ...prev, isOpen: false }));
    }
  };
  
  // Handle initial generation click (opens modal)
  const handleInitialGenerateClick = () => {
    setIsContextModalOpen(true);
  };
  
  // Handle context modal submission
  const handleContextModalSubmit = async (context: UserContext) => {
    // Build context string from the 3 questions
    const contextString = `
Progress: ${context.progressSoFar}
Challenge: ${context.biggestChallenge}
Next Steps: ${context.nextSteps}
    `.trim();
    
    // Update the context input with the combined answers
    if (onContextInputChange) {
      onContextInputChange(contextString);
    }
    
    // Close modal
    setIsContextModalOpen(false);
    
    // Trigger generation directly
    if (!openaiThreadId) {
      toast.error("Cannot generate tasks - goal missing OpenAI thread");
      return;
    }
    
    try {
      await bulkGenerationMutation.mutateAsync({
        goalId,
        goalTitle,
        goalDescription,
        openaiThreadId,
        userContext: contextString
      });
    } catch (error) {
      console.error('Generate tasks error:', error);
    }
  };



  // Prepare feedback props
  const getFeedbackProps = () => {
    const { selectedTasks } = feedbackState;
    
    if (selectedTasks.length === 1) {
      return {
        taskTitle: selectedTasks[0].title,
        taskCount: 1
      };
    } else {
      return {
        taskTitles: selectedTasks.map(task => task.title),
        taskCount: selectedTasks.length
      };
    }
  };

      return (
      <div className={`relative ${className || ''}`}>
        <TaskRecommendationImporter
          goalId={goalId}
          goalTitle={goalTitle}
          goalDescription={goalDescription}
          openaiThreadId={openaiThreadId}
          onBulkAddToProject={handleBulkAddToProject}
          onBulkClearSelection={handleBulkClearSelection}
          className="h-full"
          isProcessingFeedback={feedbackState.isProcessing}
          hideClearSelection={hideClearSelection}
          contextInput={contextInput}
          onContextInputChange={onContextInputChange}
          hideContextInput={isInitialGeneration}
          onGenerateClick={isInitialGeneration ? handleInitialGenerateClick : undefined}
          isExternallyGenerating={bulkGenerationMutation.isPending}
        />
        
        {/* Enhanced Feedback Dropdown */}
        <EnhancedFeedbackDropdown
          isOpen={feedbackState.isOpen}
          onClose={handleCloseFeedback}
          onSubmit={handleFeedbackSubmit}
          type={feedbackState.type}
          isProcessing={feedbackState.isProcessing}
          {...getFeedbackProps()}
        />
        
        {/* Task Generation Context Modal (for initial generation) */}
        {isInitialGeneration && (
          <TaskGenerationContextModal
            isOpen={isContextModalOpen}
            onClose={() => setIsContextModalOpen(false)}
            onSubmit={handleContextModalSubmit}
            goalTitle={goalTitle}
          />
        )}
      </div>
    );
}; 